import { useSite } from '@/hooks/use-site'
import { Button } from '@blogging/ui'
import { slugify } from '@blogging/utils'
import {
  ArrowLeft,
  CircleCheck,
  Eye,
  Loader2,
  PanelRight,
  Save,
  Send,
} from 'lucide-react'
import { forwardRef, useImperativeHandle } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

export interface PostFormRef {
  save: () => void
  publish: () => void
}

interface PostFormProps {
  onSubmit: (data: any) => void
  isSubmitting: boolean
  siteId: string
  isSidebarOpen: boolean
  toggleSidebar: () => void
  saveNotification: string | null
}

const PostForm = forwardRef<PostFormRef, PostFormProps>(
  (
    {
      onSubmit,
      isSubmitting,
      siteId,
      isSidebarOpen,
      toggleSidebar,
      saveNotification,
    },
    ref,
  ) => {
    const navigate = useNavigate()
    const { siteUrlWithScheme } = useSite()

    const { register, handleSubmit, formState, setValue, watch, getValues } =
      useFormContext<any>()

    // Watch values needed for logic
    const slug = watch('slug')
    const status = watch('status')
    const type = watch('type')

    // Expose save and publish methods via ref
    useImperativeHandle(ref, () => ({
      save: () => {
        handleSubmit(onSubmit)()
      },
      publish: () => {
        if (status === 'published') {
          handleSubmit(onSubmit)()
        } else {
          if (confirm('Are you sure you want to publish this post?')) {
            setValue('status', 'published')
            onSubmit(getValues())
          }
        }
      },
    }))

    // Auto-generate slug from title on blur when slug is empty
    const handleTitleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const title = e.target.value.trim()
      if (title && !slug) {
        const generatedSlug = slugify(title)
        setValue('slug', generatedSlug, { shouldDirty: true })
      }
    }

    return (
      <form
        id="post-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 mt-8 w-full"
      >
        <div className="w-full">
          <textarea
            {...register('title')}
            id="title"
            onBlur={handleTitleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const textEditor = document.querySelector(
                  '.ProseMirror',
                ) as HTMLElement
                if (textEditor) textEditor.focus()
              }
            }}
            placeholder="Enter your title..."
            className="break-normal text-wrap font-semibold w-full text-3xl sm:text-5xl leading-tight border-none bg-transparent placeholder:text-muted-foreground focus-visible:outline-none resize-none min-h-auto no-scrollbar overflow-hidden"
            tabIndex={1}
          />
        </div>
        <header
          className="absolute top-0 right-0 left-0 gap-1 sm:gap-2 flex justify-between items-center"
        >
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              tabIndex={3}
              onClick={() => {
                if (type === 'post') navigate(`/${siteId}/posts`)
                else navigate(`/${siteId}/pages`)
              }}
            >
              <ArrowLeft />
              {type === 'post' ? 'Posts' : 'Pages'}
            </Button>
            {saveNotification && (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground animate-in fade-in duration-300">
                <CircleCheck className="h-4 w-4 text-green-500 shrink-0" />
                <span className="hidden sm:inline">{saveNotification}</span>
              </span>
            )}
          </div>

          <div className="flex gap-1 sm:gap-2 items-center">
            <Button
              type="submit"
              variant="outline"
              tabIndex={5}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 />
              ) : (
                <Save />
              )}
              <span className="hidden sm:inline">
                {status === 'published' ? 'Update' : 'Save'}
              </span>
            </Button>
            {slug && (
              <a
                href={`${siteUrlWithScheme}/${slug}`}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:block"
              >
                <Button type="button" variant="outline" tabIndex={6}>
                  <Eye />
                  {status === 'published' ? 'View post' : 'Preview'}
                </Button>
              </a>
            )}
            {status !== 'published' ? (
              <Button
                type="button"
                variant="default"
                tabIndex={4}
                disabled={isSubmitting}
                onClick={() => {
                  if (
                    confirm('Are you sure you want to publish this post?')
                  ) {
                    setValue('status', 'published')
                    onSubmit(getValues())
                  }
                }}
              >
                {isSubmitting ? (
                  <Loader2 />
                ) : (
                  <Send />
                )}
                <span className="hidden sm:inline">Publish</span>
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                tabIndex={5}
                disabled={isSubmitting}
                onClick={() => {
                  if (
                    confirm('Are you sure you want to unpublish this post?')
                  ) {
                    setValue('status', 'draft')
                    onSubmit(getValues())
                  }
                }}
              >
                {isSubmitting ? (
                  <Loader2 />
                ) : (
                  <Save />
                )}
                <span className="hidden sm:inline">Unpublish</span>
              </Button>
            )}
            {!isSidebarOpen && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="border border-gray-200 shadow"
                onClick={toggleSidebar}
                aria-label="Open sidebar"
                tabIndex={7}
              >
                <PanelRight className="h-5 w-5" />
              </Button>
            )}
          </div>
        </header>
      </form>
    )
  },
)

PostForm.displayName = 'PostForm'

export default PostForm
